/**
 * Ported VERBATIM from apr_template `src/pages/DispositionCostAnalysis.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import { listDispositions, type AppDisposition } from '../compat/dispositionsApi';
import './ApplicationDependencies.css';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs;
// disposition chips keep the Disposition Matrix fill.
import { Banner, FactList, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CARD, WS_CHIP, WS_COLOR, WS_CONTROL, WS_RADIUS, wsTone, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';
import { dispositionColor as matrixColor, matrixName } from '../dispositionPalette';
import { DispositionChip } from '../compat/DispositionChip';
import { GroupMultiSelect, ALL_GROUPS_LABEL, inPicked } from '../workspace/GroupMultiSelect';

const DispositionCostAnalysisHelp: Help = {
  title: 'Disposition Cost Analysis',
  icon: '💰',
  purpose: 'Estimate the one-time cost of disposing each application, broken down by implementation phase. These costs feed the business case as the up-front investment against projected savings.',
  doHere: [
    'Pick one or more <b>Integration Groups</b> (Ctrl-click for several), then filter by <b>App</b> or <b>Disposition</b>; the KPI cards, group subtotals and totals row update to match.',
    'Switch between <b>By Phase</b> (cost per app and phase) and <b>By Resource</b> (cost per role and phase).',
    'Click <b>Recalculate All</b> to rebuild costs from phase effort planning data for every application.',
    'Click a row (or <b>View</b>) to open the detail drawer with the full phase breakdown.',
    'In the drawer, edit the <b>Notes / Justification</b> and save, or <b>Recalculate</b> a single app.',
    'Use <b>Download CSV</b> to export the current filtered view.',
  ],
  fits: 'Integration &rarr; Business Case &rarr; Disposition Cost Analysis',
  firstTime: 'If the table is empty, click Recalculate All to populate costs from each application&rsquo;s phase effort planning.',
  sections: [
    {
      type: 'defs',
      label: 'Cost columns',
      items: [
        { term: 'Assessment & Planning', color: '#2563eb', def: 'Cost of scoping and preparation.' },
        { term: 'Design & Architecture', color: '#7c3aed', def: 'Cost of target design work.' },
        { term: 'Migration Execution', color: '#16a34a', def: 'Cost of the core migration/build.' },
        { term: 'Testing & Validation', color: '#0f766e', def: 'Cost of verification before go-live.' },
        { term: 'Go-Live & Optimization', color: '#dc2626', def: 'Cost of cutover and tuning.' },
        { term: 'One-Time Disposition', color: '#1565c0', def: 'Sum of all phase costs &mdash; the total up-front spend for the app.' },
      ],
      note: 'Costs are derived from phase duration, FTE allocation, and daily rates.',
    },
  ],
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Shape returned by GET /api/disposition-cost-analysis.
 * `integration_group_id` is present on records but intentionally ignored
 * client-side. When multiple records exist per application_id_code (one per
 * historical integration_group_id), we collapse to the most-recently-calculated
 * record so each app appears exactly once.
 */
interface CostRecord {
  application_id_code: string;
  application_name?: string;
  integration_group_id?: string | null;
  group_name?: string | null;
  recommendation?: string | null;
  assessment_planning_cost?: number | string;
  design_architecture_cost?: number | string;
  migration_execution_cost?: number | string;
  testing_validation_cost?: number | string;
  golive_optimization_cost?: number | string;
  total_cost?: number | string;
  justification?: string;
  last_calculated_date?: string;
  updated_at?: string;
  // integration_group_id intentionally not typed — ignored client-side
  [key: string]: unknown;
}

/**
 * GET /api/disposition-cost-analysis/resources — one line per (group, app, phase, pool):
 * duration × FTE × rate. Owner request 2026-11-04: cost per resource.
 */
interface ResourceLine {
  integration_group_id: string | null;
  group_name?: string | null;
  application_id_code: string;
  phase_name: string;
  duration_days: number | string;
  resource_pool_id: number;
  resource_type: string;
  resource_location: string;
  cost_per_day: number | string;
  allocated_fte: number | string;
  cost: number | string;
}

const PHASE_KEYS = ['Assessment_Planning', 'Design_Architecture', 'Migration_Execution', 'Testing_Validation', 'GoLive_Optimization'] as const;
const PHASE_LABELS = ['Assessment &<br/>Planning', 'Design &<br/>Architecture', 'Migration<br/>Execution', 'Testing &<br/>Validation', 'Go-Live &<br/>Optimization'];
const rowKey = (groupId: string | null | undefined, appCode: string | null | undefined) => `${groupId || ''}|${appCode || ''}`;

/** Shape returned by GET /api/deployment-status */
interface DeploymentStatusRecord {
  integration_group_id?: string | null;
  application_id: string;
  application_id_code?: string;
  deployment_status: string;
}

/** Merged view: one row per (integration group, application) — owner request 2026-11-04. */
interface AppRow {
  id: string;                     // rowKey(group, app)
  integration_group_id?: string;
  group_name?: string;
  application_id_code?: string;
  application_name: string;
  vendor?: string;
  l1_capability?: string;
  disposition?: string;           // final_disposition from dispositions API
  // Phase cost columns (may be absent if no cost record → shown as —)
  assessment_planning_cost?: number;
  design_architecture_cost?: number;
  migration_execution_cost?: number;
  testing_validation_cost?: number;
  golive_optimization_cost?: number;
  /** One-time disposition cost = total_cost from cost record */
  one_time_disposition?: number;
  /**
   * One-time integration cost: not returned by current endpoint → always undefined.
   * Displayed as — in the table.
   */
  one_time_integration?: number;
  /**
   * Annual savings (mid): not returned by current endpoint → always undefined.
   * Displayed as — in the table.
   */
  annual_savings_mid?: number;
  justification?: string;
  last_calculated_date?: string;
  deployment_status?: string;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

function parseCost(v?: number | string | null): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === 'number') return v;
  return parseFloat(v) || 0;
}

function fmtCurrency(v?: number): string {
  if (v === undefined || v === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
}

function fmtPayback(oneTime?: number, annualMid?: number): string {
  if (!oneTime || !annualMid || annualMid === 0) return '—';
  return `${(oneTime / (annualMid / 12)).toFixed(1)} mo`;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// DW: a disposition chip is FILLED in the Disposition Matrix colour (§1.9.3 rules 2-3); a
// value the matrix does not name (Pending, NEEDS_REVIEW) is the neutral pair.
function dispositionChipStyle(d?: string | null): React.CSSProperties {
  if (!d || !matrixName(d)) return { ...WS_CHIP, ...wsTone('neutral') };
  const c = matrixColor(d);
  return { ...WS_CHIP, background: c.base, color: c.onBase };
}

function deploymentStatusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'Need Approval':  return wsTone('warning');
    case 'Approved':       return wsTone('info');
    case 'In Development': return wsTone('info');
    case 'Deployed':       return wsTone('success');
    default:               return wsTone('neutral');
  }
}

/** Numbers in table columns: right-aligned, tabular figures. */
const TABULAR: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const SECTION_LABEL: React.CSSProperties = {
  ...wsType('label'), fontWeight: 600, color: WS_COLOR.textMuted, marginBottom: 8,
};

/**
 * Collapse multiple cost records per application (from different historical
 * integration_group_ids) — keep the most-recently-calculated one.
 */
function collapseCostRecords(records: CostRecord[]): Map<string, CostRecord> {
  const byApp = new Map<string, CostRecord>();
  for (const r of records) {
    const key = r.application_id_code;
    if (!key) continue;
    const existing = byApp.get(key);
    if (!existing) {
      byApp.set(key, r);
      continue;
    }
    const existingDate = existing.last_calculated_date
      ? new Date(existing.last_calculated_date).getTime()
      : existing.updated_at ? new Date(existing.updated_at as string).getTime() : 0;
    const newDate = r.last_calculated_date
      ? new Date(r.last_calculated_date).getTime()
      : r.updated_at ? new Date(r.updated_at as string).getTime() : 0;
    if (newDate > existingDate) byApp.set(key, r);
  }
  return byApp;
}

/**
 * One row per (integration group, application) cost record, enriched with APR's disposition data.
 * PORT NOTE (owner request 2026-11-04): the source listed every application in the portfolio
 * (579 rows, nearly all empty) and collapsed an app's records from several groups into one; the
 * screen now lists exactly the (group, app) pairs that have cost data, each on its own row.
 */
function mergeRows(
  dispositions: AppDisposition[],
  costRecords: CostRecord[],
  deploymentStatusMap: Map<string, string>
): AppRow[] {
  const dispByCode = new Map(dispositions.filter(d => d.application_id_code).map(d => [d.application_id_code as string, d]));
  // Owner ruling 2026-11-04: "I do not need to see any app / record that has no disposition costs."
  return costRecords.filter(cost => parseCost(cost.total_cost) > 0).map((cost): AppRow => {
    const key = cost.application_id_code || '';
    const d = dispByCode.get(key);
    const groupId = cost.integration_group_id || undefined;
    return {
      id: rowKey(groupId, key),
      integration_group_id: groupId,
      group_name: cost.group_name || undefined,
      application_id_code: key,
      application_name: d?.application_name || cost.application_name || key,
      vendor: d?.vendor,
      l1_capability: d?.l1_business_functional_area,
      disposition: d?.final_disposition || cost.recommendation || undefined,
      assessment_planning_cost: parseCost(cost.assessment_planning_cost),
      design_architecture_cost: parseCost(cost.design_architecture_cost),
      migration_execution_cost: parseCost(cost.migration_execution_cost),
      testing_validation_cost:  parseCost(cost.testing_validation_cost),
      golive_optimization_cost: parseCost(cost.golive_optimization_cost),
      one_time_disposition: parseCost(cost.total_cost),
      one_time_integration: undefined,   // not provided by current endpoint
      annual_savings_mid: undefined,     // not provided by current endpoint
      justification: cost.justification,
      last_calculated_date: cost.last_calculated_date,
      deployment_status: deploymentStatusMap.get(rowKey(groupId, key)) || deploymentStatusMap.get(key) || (d ? deploymentStatusMap.get(d.id) : undefined) || undefined,
    };
  }).sort((a, b) => (a.integration_group_id || '').localeCompare(b.integration_group_id || '') || (a.application_id_code || '').localeCompare(b.application_id_code || ''));
}

/** Cost per role: lines of one (group) summed by resource pool, with a cost per phase. */
interface RoleCost {
  pool: number; role: string; location: string; rate: number;
  phase: number[]; fte: number[]; days: number; total: number;
}
function rollupByRole(lines: ResourceLine[]): RoleCost[] {
  const byPool = new Map<number, RoleCost>();
  for (const l of lines) {
    let r = byPool.get(l.resource_pool_id);
    if (!r) {
      r = { pool: l.resource_pool_id, role: l.resource_type, location: l.resource_location, rate: parseCost(l.cost_per_day), phase: [0, 0, 0, 0, 0], fte: [0, 0, 0, 0, 0], days: 0, total: 0 };
      byPool.set(l.resource_pool_id, r);
    }
    const i = PHASE_KEYS.indexOf(l.phase_name as typeof PHASE_KEYS[number]);
    const cost = parseCost(l.cost);
    const fte = parseCost(l.allocated_fte);
    if (i >= 0) { r.phase[i] = (r.phase[i] ?? 0) + cost; r.fte[i] = (r.fte[i] ?? 0) + fte; }
    r.days += fte * parseCost(l.duration_days);
    r.total += cost;
  }
  return Array.from(byPool.values()).sort((a, b) => b.total - a.total);
}

/** Bold group ID with its name beneath — as Roadmap Schedule and Effort Planning show it. */
function GroupCell({ id, name }: { id?: string; name?: string }) {
  return (
    <div>
      <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{id || '—'}</div>
      {name && <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>{name}</div>}
    </div>
  );
}

function downloadCsv(rows: AppRow[]) {
  const headers = [
    'Application', 'App ID Code', 'Vendor', 'L1 Capability', 'Disposition',
    'Deployment Status',
    'Assessment & Planning $', 'Design & Architecture $', 'Migration Execution $',
    'Testing & Validation $', 'Go-Live & Optimization $',
    'One-Time Disposition $',
    'Last Calculated', 'Justification',
  ];
  const esc = (v?: string | null) => `"${(v || '').replace(/"/g, '""')}"`;
  const numOrEmpty = (v?: number) => (v !== undefined ? String(v) : '');
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      esc(r.application_name),
      esc(r.application_id_code),
      esc(r.vendor),
      esc(r.l1_capability),
      esc(r.disposition),
      esc(r.deployment_status),
      numOrEmpty(r.assessment_planning_cost),
      numOrEmpty(r.design_architecture_cost),
      numOrEmpty(r.migration_execution_cost),
      numOrEmpty(r.testing_validation_cost),
      numOrEmpty(r.golive_optimization_cost),
      numOrEmpty(r.one_time_disposition),
      r.last_calculated_date ? new Date(r.last_calculated_date).toISOString() : '',
      esc(r.justification),
    ].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `disposition_cost_analysis_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* MultiSelect chip component                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  renderOption?: (v: string) => React.ReactNode;
  placeholder?: string;
}

function MultiSelectChips({
  label, options, selected, onChange, renderOption, placeholder = 'All',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v]);
  };

  const filteredOpts = options.filter(o =>
    o.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="rd-btn rd-btn-ghost"
        style={{
          display: 'flex', alignItems: 'center', gap: 6, minWidth: 120,
          border: selected.length ? `1px solid ${WS_COLOR.primary}` : undefined,
          color: selected.length ? WS_COLOR.primary : undefined,
        }}
        onClick={() => { setOpen(v => !v); setSearch(''); }}
      >
        <Icon icon={icons.Filter} size={16} />
        {label}
        {selected.length > 0 && (
          <span style={{
            background: WS_COLOR.primary, color: WS_COLOR.onPrimary,
            borderRadius: '50%', width: 18, height: 18,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            ...wsType('meta'), fontWeight: 600, flexShrink: 0,
          }}>
            {selected.length}
          </span>
        )}
        {/* DW: expand_less / expand_more → kit chevrons (the curated set has no ChevronUp). */}
        <Icon icon={open ? icons.ChevronDown : icons.ChevronRight} size={16} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, marginTop: 4,
          background: WS_COLOR.surface, border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field,
          boxShadow: WS_COLOR.menuShadow,
          minWidth: 240, maxHeight: 320, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${WS_COLOR.border}` }}>
            <input
              className="rd-input"
              type="search"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%' }}
              autoFocus
            />
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOpts.length === 0 && (
              <div style={{ padding: '12px 14px', ...wsType('label'), color: WS_COLOR.textMuted }}>
                No matches
              </div>
            )}
            {filteredOpts.map(opt => (
              <label
                key={opt}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 14px', cursor: 'pointer', ...wsType('label'),
                  background: selected.includes(opt) ? WS_COLOR.surfaceSubtle : undefined,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  style={{ cursor: 'pointer' }}
                />
                {renderOption ? renderOption(opt) : opt}
              </label>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '8px 14px', borderTop: `1px solid ${WS_COLOR.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            {selected.length > 0 && (
              <button
                className="rd-btn rd-btn-ghost"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Detail Drawer                                                                */
/* ─────────────────────────────────────────────────────────────────────────── */

interface DrawerProps {
  row: AppRow;
  /** This (group, app)'s resource lines — the per-resource breakdown. */
  lines: ResourceLine[];
  onClose: () => void;
  onJustificationSaved: (appIdCode: string, justification: string) => void;
}

function DetailDrawer({ row, lines, onClose, onJustificationSaved }: DrawerProps) {
  const roles = rollupByRole(lines);
  const [justification, setJustification] = useState(row.justification || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcError, setRecalcError] = useState('');
  const [recalcSuccess, setRecalcSuccess] = useState('');
  const [wide, setWide] = useState(false);

  const totalOneTime =
    (row.one_time_disposition !== undefined || row.one_time_integration !== undefined)
      ? (row.one_time_disposition ?? 0) + (row.one_time_integration ?? 0)
      : undefined;

  const hasCostData = row.one_time_disposition !== undefined;

  const handleSaveJustification = async () => {
    if (!row.application_id_code) return;
    setSaving(true);
    setSaveError('');
    try {
      const response = await authFetch(
        `/api/disposition-cost-analysis/${row.application_id_code}/justification`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ justification, integration_group_id: row.integration_group_id }),
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to save justification');
      }
      onJustificationSaved(row.application_id_code, justification);
    } catch (e: any) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    if (!row.application_id_code) return;
    setRecalculating(true);
    setRecalcError('');
    setRecalcSuccess('');
    try {
      const response = await authFetch(
        `/api/disposition-cost-analysis/calculate/${row.application_id_code}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ integration_group_id: row.integration_group_id }) }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to recalculate');
      }
      setRecalcSuccess('Recalculated successfully. Refresh the page to see updated costs.');
    } catch (e: any) {
      setRecalcError(e.message || 'Recalculate failed');
    } finally {
      setRecalculating(false);
    }
  };

  // DW: the fixed-overlay drawer is a kit Sheet (right panel). Its title is the application
  // name; the header's Save and Maximize controls sit at the top of the body, so the hint
  // "Save icon at the top" stays true. Maximize doubles the panel (1062) instead of 94vw.
  return (
    <Sheet open title={row.application_name} onClose={onClose} width={wide ? 1062 : 531}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {row.integration_group_id && (
            <span style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>
              {row.integration_group_id}
            </span>
          )}
          {row.application_id_code && (
            <span style={{ ...wsType('mono'), color: WS_COLOR.textMuted }}>
              {row.application_id_code}
            </span>
          )}
          {row.disposition
            ? <span style={dispositionChipStyle(row.disposition)}>{row.disposition}</span>
            : <span style={dispositionChipStyle(null)}>Pending</span>
          }
          {row.deployment_status && (
            <span style={{ ...WS_CHIP, ...deploymentStatusStyle(row.deployment_status) }}>
              {row.deployment_status}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {row.application_id_code && (
            <IconButton icon={icons.Save} label="Save" title="Save notes" onClick={handleSaveJustification} disabled={saving} />
          )}
          <IconButton
            icon={wide ? icons.ArrowRight : icons.ArrowLeft}
            label="Maximize"
            title={wide ? 'Restore' : 'Maximize'}
            onClick={() => setWide((w) => !w)}
          />
        </div>
      </div>

      {/* App details */}
      <div style={{ marginBottom: 20 }}>
        <FactList
          items={[
            { label: 'Integration Group', value: row.integration_group_id ? `${row.integration_group_id} - ${row.group_name || ''}` : '—' },
            { label: 'Vendor', value: row.vendor || '—' },
            { label: 'L1 Capability', value: row.l1_capability || '—' },
            { label: 'Last Calculated', value: fmtDate(row.last_calculated_date) },
          ]}
        />
      </div>

      {/* Cost breakdown */}
      <div style={{ marginBottom: 20 }}>
        <div style={SECTION_LABEL}>
          Phase Cost Breakdown
        </div>

        {!hasCostData ? (
          <div style={{
            ...WS_CARD, padding: '16px 14px', background: WS_COLOR.surfaceSubtle,
            ...wsType('label'), fontWeight: 400, color: WS_COLOR.textMuted, textAlign: 'center',
          }}>
            No cost data calculated yet. Use the Recalculate button below.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Assessment & Planning',  row.assessment_planning_cost],
                ['Design & Architecture',  row.design_architecture_cost],
                ['Migration Execution',    row.migration_execution_cost],
                ['Testing & Validation',   row.testing_validation_cost],
                ['Go-Live & Optimization', row.golive_optimization_cost],
              ].map(([label, val]) => (
                <tr key={label as string}>
                  <td style={{ color: WS_COLOR.textMuted }}>{label}</td>
                  <td style={{ ...TABULAR, fontWeight: 500 }}>
                    {val !== undefined ? fmtCurrency(val as number) : '—'}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ fontWeight: 600, borderTop: `2px solid ${WS_COLOR.text}` }}>One-Time Disposition Total</td>
                <td style={{ ...TABULAR, fontWeight: 600, color: WS_COLOR.primary, borderTop: `2px solid ${WS_COLOR.text}` }}>
                  {fmtCurrency(row.one_time_disposition)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Cost by resource — owner request 2026-11-04 */}
      <div style={{ marginBottom: 20 }} data-testid="dca-drawer-resources">
        <div style={SECTION_LABEL}>
          Cost by Resource
        </div>
        {roles.length === 0 ? (
          <div style={{ ...WS_CARD, padding: '16px 14px', background: WS_COLOR.surfaceSubtle, ...wsType('label'), fontWeight: 400, color: WS_COLOR.textMuted, textAlign: 'center' }}>
            No resources allocated to this application in this group.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Role</th>
                {wide && PHASE_LABELS.map((h, i) => <th key={i} style={{ textAlign: 'right' }} dangerouslySetInnerHTML={{ __html: h }} />)}
                <th style={{ textAlign: 'right' }}>Person-days</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(r => (
                <tr key={r.pool}>
                  <td>
                    <div style={{ fontWeight: 500, color: WS_COLOR.text }}>{r.role}</div>
                    <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{r.location} · {fmtCurrency(r.rate)}/day</div>
                  </td>
                  {wide && r.phase.map((c, i) => <td key={i} style={TABULAR}>{c ? fmtCurrency(c) : '—'}</td>)}
                  <td style={TABULAR}>{r.days.toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                  <td style={{ ...TABULAR, fontWeight: 500 }}>{fmtCurrency(r.total)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ fontWeight: 600, borderTop: `2px solid ${WS_COLOR.text}` }}>Total</td>
                {wide && PHASE_KEYS.map((_, i) => <td key={i} style={{ ...TABULAR, fontWeight: 600, borderTop: `2px solid ${WS_COLOR.text}` }}>{fmtCurrency(roles.reduce((s2, r) => s2 + (r.phase[i] ?? 0), 0))}</td>)}
                <td style={{ ...TABULAR, fontWeight: 600, borderTop: `2px solid ${WS_COLOR.text}` }}>{roles.reduce((s2, r) => s2 + r.days, 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                <td style={{ ...TABULAR, fontWeight: 600, color: WS_COLOR.primary, borderTop: `2px solid ${WS_COLOR.text}` }}>{fmtCurrency(roles.reduce((s2, r) => s2 + r.total, 0))}</td>
              </tr>
            </tbody>
          </table>
        )}
        {!wide && roles.length > 0 && (
          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 6 }}>Maximize the panel to see each role's cost per phase.</div>
        )}
      </div>

      {/* Justification / notes */}
      <div style={{ marginBottom: 20 }}>
        <div style={SECTION_LABEL}>
          Notes / Justification
        </div>
        <textarea
          value={justification}
          onChange={e => setJustification(e.target.value)}
          placeholder="Enter justification or notes for this disposition cost…"
          rows={4}
          style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
        {saveError && (
          <div style={{ marginTop: 4 }}><Banner tone="error">{saveError}</Banner></div>
        )}
        <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          Use the <Icon icon={icons.Save} size={16} /> Save icon at the top to save notes.
        </div>
      </div>

      {/* Recalculate */}
      {row.application_id_code && (
        <div style={{
          borderTop: `1px solid ${WS_COLOR.border}`, paddingTop: 16,
        }}>
          <div style={SECTION_LABEL}>
            Recalculate Costs
          </div>
          {recalcError && (
            <div style={{ marginBottom: 8 }}><Banner tone="error">{recalcError}</Banner></div>
          )}
          {recalcSuccess && (
            <div style={{ marginBottom: 8 }}><Banner tone="success">{recalcSuccess}</Banner></div>
          )}
          <button
            className="rd-btn rd-btn-ghost"
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            <Icon icon={icons.RefreshCw} size={20} />
            {recalculating ? 'Recalculating…' : 'Recalculate This App'}
          </button>
          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 6 }}>
            Recalculates using phase effort planning data for this application.
          </div>
        </div>
      )}
    </Sheet>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Main Page                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

const DispositionCostAnalysis: React.FC = () => {
  const [rows, setRows] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [globalMessage, setGlobalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Multi-select filters
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [view, setView] = useState<'phase' | 'resource'>('phase');
  const [lines, setLines] = useState<ResourceLine[]>([]);
  const [appFilter, setAppFilter] = useState<string[]>([]);
  const [dispFilter, setDispFilter] = useState<string[]>([]);

  // Detail drawer
  const [drawerRow, setDrawerRow] = useState<AppRow | null>(null);

  const { confirm, dialogs } = useWsDialogs();

  /* ── load data ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dispositions, costResp, deployResp, linesResp] = await Promise.all([
        listDispositions(),
        authFetch('/api/disposition-cost-analysis').then(r =>
          r.ok ? r.json() : Promise.resolve([])
        ).catch(() => [] as CostRecord[]),
        authFetch('/api/deployment-status').then(r =>
          r.ok ? r.json() : Promise.resolve([])
        ).catch(() => {
          console.warn('deployment-status endpoint not available, skipping');
          return [] as DeploymentStatusRecord[];
        }),
        authFetch('/api/disposition-cost-analysis/resources').then(r =>
          r.ok ? r.json() : Promise.resolve([])
        ).catch(() => [] as ResourceLine[]),
      ]);

      const costRecords: CostRecord[] = Array.isArray(costResp) ? (costResp as CostRecord[]) : [];
      setLines(Array.isArray(linesResp) ? (linesResp as ResourceLine[]) : []);

      // Build deployment status map keyed by both application_id and application_id_code
      const deploymentStatusMap = new Map<string, string>();
      const deployData: DeploymentStatusRecord[] = Array.isArray(deployResp) ? deployResp : [];
      for (const ds of deployData) {
        if (ds.integration_group_id && ds.application_id) deploymentStatusMap.set(rowKey(ds.integration_group_id, ds.application_id), ds.deployment_status);
        if (ds.application_id_code) deploymentStatusMap.set(ds.application_id_code, ds.deployment_status);
        if (ds.application_id)      deploymentStatusMap.set(ds.application_id,      ds.deployment_status);
      }

      setRows(mergeRows(dispositions, costRecords, deploymentStatusMap));
    } catch (e: any) {
      setError(e?.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Refresh on window focus / tab visibility change
  useEffect(() => {
    const handleFocus = () => loadData();
    const handleVisibility = () => { if (!document.hidden) loadData(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadData]);

  const recalculateAll = async () => {
    if (!(await confirm(
      'This will recalculate costs for all applications with effort planning data. Continue?'
    ))) return;

    setRecalculating(true);
    setGlobalMessage(null);
    try {
      const response = await authFetch('/api/disposition-cost-analysis/calculate-all', {
        method: 'POST',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to recalculate costs');
      }
      const result = await response.json();
      let msg = `Processed ${result.applications_found || 0} application(s).`;
      if (result.processed > 0) msg += ` Calculated costs for ${result.processed}.`;
      if (result.skipped > 0)   msg += ` Skipped ${result.skipped}.`;
      setGlobalMessage({ type: 'success', text: msg });
      await loadData();
    } catch (e: any) {
      setGlobalMessage({ type: 'error', text: e.message || 'Recalculate failed' });
    } finally {
      setRecalculating(false);
    }
  };

  /* ── Update a row's justification in-place after drawer save ── */
  const handleJustificationSaved = (appIdCode: string, justification: string) => {
    const id = drawerRow?.id;
    setRows(prev => prev.map(r =>
      r.id === id ? { ...r, justification } : r
    ));
    if (drawerRow?.application_id_code === appIdCode) {
      setDrawerRow(prev => prev ? { ...prev, justification } : null);
    }
  };

  /* ── distinct options for filters ── */
  const appOptions = Array.from(new Set(rows.map(r => r.application_name))).sort();
  const rawDispositionSet = new Set(rows.map(r => r.disposition).filter(Boolean)) as Set<string>;
  const dispOptions: string[] = [
    ...Array.from(rawDispositionSet).sort(),
    'Pending',
  ];

  /* ── filtering ── */
  const groupOptions = Array.from(new Map(rows.filter(r => r.integration_group_id).map(r => [r.integration_group_id as string, r.group_name])).entries())
    .map(([id, description]) => ({ id, description })).sort((a, b) => a.id.localeCompare(b.id));
  const filtered = rows.filter(r => {
    if (!inPicked(groupFilter, r.integration_group_id)) return false;
    if (appFilter.length > 0 && !appFilter.includes(r.application_name)) return false;
    if (dispFilter.length > 0) {
      const effective = r.disposition || 'Pending';
      if (!dispFilter.includes(effective)) return false;
    }
    return true;
  });

  /* ── stat calculations (over filtered rows) ── */
  const totalApps       = filtered.length;
  const totalOneTimeDisp = filtered.reduce((s, r) => s + (r.one_time_disposition ?? 0), 0);
  const totalOneTimeInt  = filtered.reduce((s, r) => s + (r.one_time_integration  ?? 0), 0);
  const totalOneTime     = totalOneTimeDisp + totalOneTimeInt;
  const totalAnnualMid   = filtered.reduce((s, r) => s + (r.annual_savings_mid    ?? 0), 0);
  const portfolioPayback =
    totalAnnualMid > 0 ? (totalOneTime / (totalAnnualMid / 12)).toFixed(1) + ' mo' : '—';

  /* ── totals row calc ── */
  const totalAssessment = filtered.reduce((s, r) => s + (r.assessment_planning_cost ?? 0), 0);
  const totalDesign     = filtered.reduce((s, r) => s + (r.design_architecture_cost ?? 0), 0);
  const totalMigration  = filtered.reduce((s, r) => s + (r.migration_execution_cost ?? 0), 0);
  const totalTesting    = filtered.reduce((s, r) => s + (r.testing_validation_cost  ?? 0), 0);
  const totalGoLive     = filtered.reduce((s, r) => s + (r.golive_optimization_cost ?? 0), 0);
  const totalDispositionCost = totalAssessment + totalDesign + totalMigration + totalTesting + totalGoLive;
  const avgPerApp = totalApps > 0 ? totalDispositionCost / totalApps : 0;

  /* ── by group (owner request 2026-11-04): header row with subtotals, then its apps ── */
  const groupsShown: { id: string; name?: string; rows: AppRow[] }[] = [];
  for (const r of filtered) {
    const gid = r.integration_group_id || '';
    let g = groupsShown.find(x => x.id === gid);
    if (!g) { g = { id: gid, name: r.group_name, rows: [] }; groupsShown.push(g); }
    g.rows.push(r);
  }
  const shownKeys = new Set(filtered.map(r => r.id));
  const shownLines = lines.filter(l => shownKeys.has(rowKey(l.integration_group_id, l.application_id_code)));
  const phaseSum = (rs: AppRow[]) => [
    rs.reduce((s2, r) => s2 + (r.assessment_planning_cost ?? 0), 0),
    rs.reduce((s2, r) => s2 + (r.design_architecture_cost ?? 0), 0),
    rs.reduce((s2, r) => s2 + (r.migration_execution_cost ?? 0), 0),
    rs.reduce((s2, r) => s2 + (r.testing_validation_cost ?? 0), 0),
    rs.reduce((s2, r) => s2 + (r.golive_optimization_cost ?? 0), 0),
  ];

  /* ── render states ── */
  if (loading) {
    return (
      <div className="page">
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '80px 20px', color: WS_COLOR.textMuted,
        }}>
          <div className="loading-spinner" style={{ marginBottom: 16 }} />
          <p>Loading disposition cost analysis…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page" style={{ padding: '24px 20px' }}>
        <PageHeader title="Disposition Cost Analysis" />
        <div style={{ maxWidth: 600, margin: '40px auto', textAlign: 'center' }}>
          <div style={{ marginBottom: 16, textAlign: 'left' }}><Banner tone="error">{error}</Banner></div>
          <button className="rd-btn rd-btn-primary" onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-fixed-header">
      <HelpPanel help={DispositionCostAnalysisHelp} />
      <PageHeader
        title="Disposition Cost Analysis"
        subtitle="Phase-based cost calculation for application migration based on duration, FTE allocation, and daily rates."
        actions={
          <>
            <button
              className="rd-btn rd-btn-ghost"
              onClick={() => downloadCsv(filtered)}
              disabled={filtered.length === 0}
            >
              <Icon icon={icons.Download} size={20} />
              Download CSV
            </button>
            <button className="rd-btn rd-btn-ghost" onClick={loadData} disabled={loading}>
              <Icon icon={icons.RefreshCw} size={20} />
              Refresh
            </button>
            <button
              className="rd-btn rd-btn-primary"
              onClick={recalculateAll}
              disabled={recalculating}
            >
              <Icon icon={icons.Calculator} size={20} />
              {recalculating ? 'Recalculating…' : 'Recalculate All'}
            </button>
          </>
        }
      />

      <div className="master-data-container" style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>

        {/* Global message banner */}
        {globalMessage && (
          <div style={{ marginBottom: 16 }}>
            <Banner tone={globalMessage.type} onDismiss={() => setGlobalMessage(null)}>
              {globalMessage.text}
            </Banner>
          </div>
        )}

        {/* ── KPI summary cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, margin: '16px 0', flexShrink: 0 }}>
          {([
            { label: 'Integration Groups', value: groupsShown.length.toLocaleString('en-US'), icon: icons.Layers, tone: 'info' },
            { label: 'Applications', value: totalApps.toLocaleString('en-US'), icon: icons.LayoutGrid, tone: 'info' },
            { label: 'Total Disposition Cost', value: fmtCurrency(totalDispositionCost), icon: icons.Coins, tone: 'success' },
            { label: 'Avg per Application', value: fmtCurrency(avgPerApp), icon: icons.BarChart3, tone: 'info' },
            { label: 'Migration Execution', value: fmtCurrency(totalMigration), icon: icons.Shuffle, tone: 'success' },
          ] as const).map((c) => (
            <div key={c.label} style={{ ...WS_CARD, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: WS_RADIUS.field, ...wsTone(c.tone), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon icon={c.icon} size={24} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...wsType('pageTitle'), color: WS_COLOR.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.value}</div>
                <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div className="rd-filters-bar" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 10, flexShrink: 0 }}>
          <GroupMultiSelect
            testId="group-picker"
            label="Integration Group:"
            placeholder={ALL_GROUPS_LABEL}
            groups={groupOptions}
            value={groupFilter}
            onChange={setGroupFilter}
          />
          <MultiSelectChips
            label="App"
            options={appOptions}
            selected={appFilter}
            onChange={setAppFilter}
            placeholder="All applications"
          />

          <MultiSelectChips
            label="Disposition"
            options={dispOptions}
            selected={dispFilter}
            onChange={setDispFilter}
            placeholder="All dispositions"
            renderOption={(d) => (
              <span style={dispositionChipStyle(d === 'Pending' ? null : d)}>
                {d}
              </span>
            )}
          />

          {(groupFilter.length > 0 || appFilter.length > 0 || dispFilter.length > 0) && (
            <button
              className="rd-btn rd-btn-ghost"
              onClick={() => { setGroupFilter([]); setAppFilter([]); setDispFilter([]); }}
            >
              Clear all filters
            </button>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
              Showing {filtered.length} of {rows.length} applications
            </span>
            <div role="group" aria-label="View" style={{ display: 'inline-flex', gap: 4 }} data-testid="dca-view">
              <button className={view === 'phase' ? 'rd-btn rd-btn-primary' : 'rd-btn rd-btn-ghost'} onClick={() => setView('phase')} aria-pressed={view === 'phase'}>By Phase</button>
              <button className={view === 'resource' ? 'rd-btn rd-btn-primary' : 'rd-btn rd-btn-ghost'} onClick={() => setView('resource')} aria-pressed={view === 'resource'}>By Resource</button>
            </div>
          </div>
        </div>

        {/* ── Main Table ── */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', ...WS_CARD }}>
          <table className="rd-table sticky-head-table dca-grid" style={{ borderCollapse: 'separate', borderSpacing: 0, overflow: 'visible' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ width: 200, position: 'sticky', top: 0, zIndex: 3, background: WS_COLOR.surfaceSubtle }}>{view === 'phase' ? 'Application' : 'Role'}</th>
                <th
                  colSpan={5}
                  style={{ textAlign: 'center', position: 'sticky', top: 0, zIndex: 3, background: WS_COLOR.surfaceSubtle, borderBottom: `1px solid ${WS_COLOR.border}` }}
                >
                  Phase Costs (USD)
                </th>
                {view === 'resource' && (
                  <th rowSpan={2} style={{ textAlign: 'right', width: 100, position: 'sticky', top: 0, zIndex: 3, background: WS_COLOR.surfaceSubtle }}>
                    Person-<br />days
                  </th>
                )}
                <th rowSpan={2} style={{ textAlign: 'right', width: 120, position: 'sticky', top: 0, zIndex: 3, background: WS_COLOR.surfaceSubtle }}>
                  Total<br />Disposition $
                </th>
                <th rowSpan={2} style={{ width: 70, position: 'sticky', top: 0, zIndex: 3, background: WS_COLOR.surfaceSubtle }}></th>
              </tr>
              <tr>
                {/* DW: the second header row sits under the first, whose height is the kit row height (--ws-row-h). */}
                {['Assessment &<br/>Planning', 'Design &<br/>Architecture', 'Migration<br/>Execution', 'Testing &<br/>Validation', 'Go-Live &<br/>Optimization'].map((h, i) => (
                  <th key={i} style={{ textAlign: 'right', width: 150, minWidth: 150, position: 'sticky', top: WS_CONTROL.rowHeight, zIndex: 2, background: WS_COLOR.surfaceSubtle }} dangerouslySetInnerHTML={{ __html: h }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={view === 'resource' ? 9 : 8} style={{ textAlign: 'center', color: WS_COLOR.textMuted, padding: '40px 0' }}>
                    {rows.length === 0
                      ? 'No applications found. Run cost calculations to populate this table.'
                      : 'No applications match the current filters.'
                    }
                  </td>
                </tr>
              )}
              {view === 'phase' && groupsShown.map(g => {
                const sub = phaseSum(g.rows);
                return (
                  <React.Fragment key={g.id}>
                    <tr data-testid="dca-group-row" style={{ background: WS_COLOR.surfaceSubtle }}>
                      <td><GroupCell id={g.id} name={g.name} /></td>
                      {sub.map((c, i) => <td key={i} style={{ ...TABULAR, fontWeight: 600 }}>{fmtCurrency(c)}</td>)}
                      <td style={{ ...TABULAR, fontWeight: 600, background: WS_COLOR.surfaceSubtle }}>{fmtCurrency(sub.reduce((a, b) => a + b, 0))}</td>
                      <td style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'right', whiteSpace: 'nowrap' }}>{g.rows.length} app{g.rows.length !== 1 ? 's' : ''}</td>
                    </tr>
                    {g.rows.map(row => (
                      <tr
                        key={row.id}
                        data-testid="dca-app-row"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setDrawerRow(row)}
                      >
                        {/* Black app ID with the disposition chip after it, name beneath */}
                        <td style={{ minWidth: 200, width: 'auto', paddingLeft: 24 }}>
                          <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, display: 'flex', alignItems: 'center' }}>
                            {row.application_id_code}
                            <DispositionChip value={row.disposition} />
                          </div>
                          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>
                            {row.application_name}
                          </div>
                        </td>
                        {[row.assessment_planning_cost, row.design_architecture_cost, row.migration_execution_cost, row.testing_validation_cost, row.golive_optimization_cost].map((c, i) => (
                          <td key={i} style={TABULAR}>
                            {c !== undefined && c !== 0 ? fmtCurrency(c) : <span className="rd-cell-muted">—</span>}
                          </td>
                        ))}
                        <td style={{ ...TABULAR, background: WS_COLOR.surfaceSubtle }}>
                          {row.one_time_disposition !== undefined
                            ? fmtCurrency(row.one_time_disposition)
                            : <span className="rd-cell-muted">—</span>
                          }
                        </td>
                        <td
                          style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            className="rd-btn rd-btn-ghost"
                            onClick={() => setDrawerRow(row)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {view === 'resource' && groupsShown.map(g => {
                const roles = rollupByRole(shownLines.filter(l => (l.integration_group_id || '') === g.id));
                const sub = PHASE_KEYS.map((_, i) => roles.reduce((s2, r) => s2 + (r.phase[i] ?? 0), 0));
                return (
                  <React.Fragment key={g.id}>
                    <tr data-testid="dca-group-row" style={{ background: WS_COLOR.surfaceSubtle }}>
                      <td><GroupCell id={g.id} name={g.name} /></td>
                      {sub.map((c, i) => <td key={i} style={{ ...TABULAR, fontWeight: 600 }}>{fmtCurrency(c)}</td>)}
                      <td style={{ ...TABULAR, fontWeight: 600 }}>{roles.reduce((s2, r) => s2 + r.days, 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                      <td style={{ ...TABULAR, fontWeight: 600, background: WS_COLOR.surfaceSubtle }}>{fmtCurrency(sub.reduce((a, b) => a + b, 0))}</td>
                      <td style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'right', whiteSpace: 'nowrap' }}>{roles.length} role{roles.length !== 1 ? 's' : ''}</td>
                    </tr>
                    {roles.map(r => (
                      <tr key={r.pool} data-testid="dca-role-row">
                        <td style={{ paddingLeft: 24 }}>
                          <div style={{ fontWeight: 600, color: WS_COLOR.text }}>{r.role}</div>
                          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>{r.location} · {fmtCurrency(r.rate)}/day</div>
                        </td>
                        {r.phase.map((c, i) => (
                          <td key={i} style={TABULAR} title={c ? `${(r.fte[i] ?? 0).toFixed(2)} FTE` : undefined}>
                            {c ? fmtCurrency(c) : <span className="rd-cell-muted">—</span>}
                          </td>
                        ))}
                        <td style={TABULAR}>{r.days.toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                        <td style={{ ...TABULAR, background: WS_COLOR.surfaceSubtle }}>{fmtCurrency(r.total)}</td>
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>

            {/* Totals footer */}
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 600, background: WS_COLOR.surfaceSubtle }}>
                  <td style={{ color: WS_COLOR.textMuted }}>
                    Totals ({filtered.length} app{filtered.length !== 1 ? 's' : ''})
                  </td>
                  <td style={TABULAR}>
                    {totalAssessment > 0 ? fmtCurrency(totalAssessment) : '—'}
                  </td>
                  <td style={TABULAR}>
                    {totalDesign > 0 ? fmtCurrency(totalDesign) : '—'}
                  </td>
                  <td style={TABULAR}>
                    {totalMigration > 0 ? fmtCurrency(totalMigration) : '—'}
                  </td>
                  <td style={TABULAR}>
                    {totalTesting > 0 ? fmtCurrency(totalTesting) : '—'}
                  </td>
                  <td style={TABULAR}>
                    {totalGoLive > 0 ? fmtCurrency(totalGoLive) : '—'}
                  </td>
                  {view === 'resource' && (
                    <td style={TABULAR}>
                      {rollupByRole(shownLines).reduce((s2, r) => s2 + r.days, 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}
                    </td>
                  )}
                  <td style={{ ...TABULAR, background: WS_COLOR.surfaceSubtle }}>
                    {totalOneTimeDisp > 0 ? fmtCurrency(totalOneTimeDisp) : '—'}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Empty state with CTA */}
        {rows.length === 0 && !loading && (
          <div style={{
            textAlign: 'center', padding: '60px 20px', color: WS_COLOR.textMuted,
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <Icon icon={icons.Coins} size={24} />
            </div>
            <div style={{ ...wsType('body'), fontWeight: 600, marginBottom: 8, color: WS_COLOR.text }}>
              No Cost Analysis Data
            </div>
            <p style={{ ...wsType('label'), fontWeight: 400, marginBottom: 20 }}>
              Run cost calculations to populate disposition cost data.
            </p>
            <button
              className="rd-btn rd-btn-primary"
              onClick={recalculateAll}
              disabled={recalculating}
            >
              {recalculating ? 'Recalculating…' : 'Calculate All Costs'}
            </button>
          </div>
        )}
      </div>

      {/* ── Detail Drawer ── */}
      {drawerRow && (
        <DetailDrawer
          key={drawerRow.id}
          row={drawerRow}
          lines={lines.filter(l => rowKey(l.integration_group_id, l.application_id_code) === drawerRow.id)}
          onClose={() => setDrawerRow(null)}
          onJustificationSaved={handleJustificationSaved}
        />
      )}
      {dialogs}
    </div>
  );
};

export default DispositionCostAnalysis;
